import { useEffect, useRef, useState } from 'react';
import { isRemote } from '../store/game';

/**
 * Eingabewert, der im Mehrspieler nicht bei jedem Tastendruck oder Schieberegler-Schritt einen Zug
 * an den Server schickt. Lokal wird sofort angezeigt, übertragen wird nach einer kurzen Pause,
 * beim Verlassen des Feldes und spätestens beim Schließen des Bildschirms.
 * Im Einzelspieler geht jede Änderung direkt durch.
 */
export function useBuffered<T>(value: T, commit: (v: T) => void, delay = 450) {
  const [local, setLocal] = useState(value);
  const localRef = useRef(value);
  const dirty = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const commitRef = useRef(commit);
  commitRef.current = commit;

  const flush = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (dirty.current) { dirty.current = false; commitRef.current(localRef.current); }
  };

  // Neuer Wert von außen (z. B. vom Server bestätigt) übernehmen, solange gerade nicht getippt wird.
  useEffect(() => { if (!dirty.current) { setLocal(value); localRef.current = value; } }, [value]);
  // Beim Schließen des Bildschirms nichts verlieren.
  useEffect(() => () => flush(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (v: T) => {
    setLocal(v); localRef.current = v;
    if (!isRemote()) { commitRef.current(v); return; }
    dirty.current = true;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, delay);
  };
  return [local, change, flush] as const;
}
